import type { CompanyTourStepResponse } from '@hellodarwin/core/lib/features/entities/companies-entities';
import type { CompanyDocumentType } from '../../features/api/slices/company-document-types-slice';
import type { OutdatedCompanyDocumentListItem } from '../../features/api/slices/company-documents-slice';
import { isOutstandingDocumentRequirement } from '../../features/document-requirements';
import type { DashboardCompanyDocumentRequest } from './document-requests-widget';

export const buildDashboardCompanyDocumentRequests = ({
  companyTourSteps,
  companyDocumentTypes,
  outdatedCompanyDocuments,
}: {
  companyTourSteps: readonly CompanyTourStepResponse[];
  companyDocumentTypes: readonly CompanyDocumentType[];
  outdatedCompanyDocuments: readonly OutdatedCompanyDocumentListItem[];
}): DashboardCompanyDocumentRequest[] => {
  type ExtendedRequirement = NonNullable<
    CompanyTourStepResponse['document_requirements']
  >[number] & {
    requirement_id?: string;
    category_code?: string;
    category_label?: string;
    document_folder_path?: string[];
    vault_folder_path?: string[];
  };
  const documentTypesByCode = new Map(
    companyDocumentTypes.map((documentType) => [
      documentType.code,
      documentType,
    ]),
  );
  const outstandingDocumentRequirements = (companyTourSteps
    .find(({ step_name }) => step_name === 'upload_company_documents')
    ?.document_requirements?.filter(isOutstandingDocumentRequirement) ??
    []) as ExtendedRequirement[];
  const requiredDocumentTypeCodes = new Set(
    outstandingDocumentRequirements.flatMap(({ document_type_code }) =>
      document_type_code ? [document_type_code] : [],
    ),
  );

  return [
    ...outstandingDocumentRequirements.map((requirement) => {
      const documentType = requirement.document_type_code
        ? documentTypesByCode.get(requirement.document_type_code)
        : undefined;

      return {
        source: 'company' as const,
        requestKind: 'required' as const,
        id: `company-required:${requirement.requirement_id ?? requirement.document_type_code}`,
        assetId: requirement.asset?.asset_id,
        documentName: requirement.name,
        requirementId: requirement.requirement_id,
        documentTypeCode: requirement.document_type_code || undefined,
        documentFolderPath:
          requirement.document_folder_path ?? requirement.vault_folder_path,
        categoryCode:
          documentType?.category_code ?? requirement.category_code ?? '',
        categoryLabel:
          documentType?.category_label ?? requirement.category_label ?? '',
        frequency: documentType?.frequency ?? ('conditional' as const),
        kind: 'file' as const,
      };
    }),
    ...outdatedCompanyDocuments
      .filter(
        ({ document_type_code }) =>
          !requiredDocumentTypeCodes.has(document_type_code),
      )
      .map((document) => ({
        source: 'company' as const,
        requestKind: 'outdated' as const,
        id: `company:${document.asset_id}`,
        assetId: document.asset_id,
        documentName: document.document_type_label,
        documentTypeCode: document.document_type_code,
        categoryCode: document.category_code,
        categoryLabel: document.category_label,
        frequency: document.frequency,
        kind: 'file' as const,
      })),
  ];
};
